import {useQuery} from "react-query";
import axios, {AxiosError} from "axios";
import {useSnackbar} from "notistack";

export interface TeleObjectBessData {
    teleObjectId: number | null;
    teleObjectDataTypeId: number | null;
    shortName: string;
    tmValue: number | null;
    unityId: number | null;
    unityName: string | null;
    teleObjectDataTypeReference: number | null;
}

export type BessTeleObjectData = TeleObjectBessData[];

export interface BessPowerRangeConfig {
    minPower: number | null;
    maxPower: number | null;
}

export interface ApiResponse<T> {
    message: string;
    data: T;
}

export interface BessTotalizers {
    normalizedCount: number;
    totalCount: number;
    activePower: number | null;
    activePowerUnity: string | null;
    exportedEnergy: number | null;
    exportedEnergyUnity: string | null;
    importedEnergy: number | null;
    importedEnergyUnity: string | null;
    powerScaleMin: number | null;
    powerScaleMax: number | null;
}

const fetchTeleObjectBess = async (equipmentId: number, powerStationId: number) => {
    try {
        return await axios.get(`api/teleobject/bess-object-type/${equipmentId}/${powerStationId}`)
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching BESS teleobject data:", error);
        return Promise.reject(error);
    }
};

export const FindTeleObjectBessReactQuery = (equipmentId?: number, powerStationId?: number) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['bessTeleObjectData', equipmentId, powerStationId],
        () => fetchTeleObjectBess(equipmentId as number, powerStationId as number),
        {
            enabled: !!equipmentId && !!powerStationId,
            onError: (error: any) => {
                enqueueSnackbar(`Erro ao carregar dados de bateria: ${error.message}`, { variant: "error" });
            },
            retry: 1,
        }
    );
};

const fetchBessPowerRangeConfig = async () => {
    try {
        return await axios.get(`api/configuration/bess-power-range`)
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching BESS power range configuration:", error);
        return Promise.reject(error);
    }
};

export const FindBessPowerRangeConfigReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery(
        ['bessPowerRangeConfig'],
        fetchBessPowerRangeConfig,
        {
            onError: (error: any) => {
                enqueueSnackbar(`Erro ao carregar configuração de potência do BESS: ${error.message}`, { variant: "error" });
            },
            retry: 1,
        }
    );
};

const fetchBessTotalizers = async (powerStationId: number): Promise<ApiResponse<BessTotalizers>> => {
    try {
        return await axios.get<ApiResponse<BessTotalizers>>(`api/bess/totalizers/${powerStationId}`)
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching BESS totalizers:", error);
        return Promise.reject(error);
    }
};

export const FindBessTotalizersReactQuery = (powerStationId?: number) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery<ApiResponse<BessTotalizers>, AxiosError>(
        ['bessTotalizers', powerStationId],
        () => fetchBessTotalizers(powerStationId as number),
        {
            enabled: !!powerStationId,
            onError: (error) => {
                enqueueSnackbar(`Erro ao carregar totalizadores do BESS: ${error.message}`, { variant: "error" });
            },
            retry: 1,
        }
    );
};
